import { useState, type FormEvent } from "react";
import { HONEYPOT_FIELD_NAME, isHoneypotFilled, submitFormUrl } from "@/utils/formSubmit";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahfl2bp14a1h3mr6krg";
const MAX_MESSAGE = 500;

const topics = [
  "提供调查线索",
  "内容纠错与补充",
  "合作与转载申请",
  "求助与资源咨询",
  "其他事项",
];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;

    if (isHoneypotFilled(formEl)) {
      setStatus("success");
      setFeedback("来信已提交，我们会在两个工作日内查看。");
      return;
    }

    if (message.trim().length === 0) {
      setStatus("error");
      setFeedback("请先填写你想说明的内容。");
      return;
    }

    if (message.length > MAX_MESSAGE) {
      setStatus("error");
      setFeedback(`内容请控制在 ${MAX_MESSAGE} 字以内。`);
      return;
    }

    setStatus("loading");
    setFeedback("");

    const result = await submitFormUrl(SUBMIT_ADDR, formEl);

    if (result.ok) {
      setStatus("success");
      setFeedback("来信已提交，我们会在两个工作日内查看。");
      formEl.reset();
      setMessage("");
    } else {
      setStatus("error");
      setFeedback(result.message || "提交未成功，请稍后重试。");
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      action={SUBMIT_ADDR}
      method="post"
      onSubmit={handleSubmit}
      className="p-5 md:p-7 rounded-lg bg-background-50 border border-background-200"
    >
      <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
        给我们写封信
      </h2>
      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
        带 <span className="text-primary-600">*</span> 的为必填项。线索类来信请尽量附上原始链接与时间信息。
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
            怎么称呼你 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            autoComplete="name"
            placeholder="昵称或称呼即可"
            className="form-field w-full px-4 py-2.5 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
            联系邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="用于接收我们的回复"
            className="form-field w-full px-4 py-2.5 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800 mb-2">
          来信主题
        </label>
        <div className="relative">
          <select
            id="contact-topic"
            name="topic"
            defaultValue={topics[0]}
            className="form-field w-full appearance-none px-4 py-2.5 pr-10 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-900 cursor-pointer"
          >
            {topics.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
          <i
            className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"
            aria-hidden="true"
          ></i>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800">
            具体内容 <span className="text-primary-600">*</span>
          </label>
          <span
            className={`text-xs ${
              message.length > MAX_MESSAGE ? "text-primary-600" : "text-foreground-500"
            }`}
          >
            {message.length} / {MAX_MESSAGE}
          </span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={6}
          maxLength={MAX_MESSAGE}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="请简要说明事情的经过、你希望我们做什么，以及可核验的材料来源。"
          className="form-field w-full px-4 py-3 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 resize-none leading-relaxed"
        ></textarea>
      </div>

      <input
        type="text"
        name={HONEYPOT_FIELD_NAME}
        className="field-verify-extra"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i
            className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}
            aria-hidden="true"
          ></i>
          {status === "loading" ? "正在提交" : "提交来信"}
        </button>
        <p className="text-xs text-foreground-500 leading-relaxed">
          提交即表示你了解本站仅基于公开信息开展分析，不会代为处理私人纠纷。
        </p>
      </div>

      {status !== "idle" && status !== "loading" && (
        <p
          role="status"
          className={`mt-4 px-4 py-3 rounded-md text-sm leading-relaxed ${
            status === "success"
              ? "bg-accent-50 text-accent-900 border border-accent-300"
              : "bg-primary-50 text-primary-900 border border-primary-300"
          }`}
        >
          {feedback}
        </p>
      )}
    </form>
  );
}